import { useCallback, useEffect, useState } from 'react';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import { httpGetCollectionsByCollectionId } from '@iconik-io/iconik-api/src/services/assets';
import { Icon } from '@iconik-io/uad';

import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import IconButton from '../../../ui/icon_button/icon_button';
import { SubclipCollectionTree } from '../subclip_collection_tree/subclip_collection_tree';

import styles from './subclip_collection_selection.module.css';

type SubclipCollectionSelectionProps = {
  collectionId: string | undefined;
  onCollectionIdChange: (collectionId: string | undefined) => void;
};

export function SubclipCollectionSelection({
  collectionId,
  onCollectionIdChange,
}: SubclipCollectionSelectionProps) {
  const dispatch = useAppDispatch();
  const [isTreeVisible, setIsTreeVisible] = useState(false);

  // TODO: Instead of entity search_items we need to use collections
  // It is not possible to do for now, because we now don't have api
  // that could return list of children collections as CollectionSchema
  const selectedCollection = useAppSelector((state) =>
    collectionId ? state.entities.search_items[collectionId] : undefined,
  );

  useEffect(() => {
    if (!collectionId) {
      return;
    }
    // Ensure that we have any potential initial collection selection in our state
    dispatch(httpGetCollectionsByCollectionId(collectionId)).catch((error) => {
      // There might be situation when user have access to an asset but doesn't
      // have access to a collection where this asset is located in.
      // In this case we need to create subclip in the root by removing
      // collectionId that was set in parent component
      if (error instanceof EndpointError) {
        if (error.response.status === 403) {
          onCollectionIdChange(undefined);
          return;
        }
      }
      throw error;
    });
  }, [collectionId, onCollectionIdChange]);

  const handleTreeNodeClick = useCallback(
    (id: string | undefined) => {
      onCollectionIdChange?.(id);
      setIsTreeVisible(false);
    },
    [onCollectionIdChange],
  );

  return (
    <div className={styles.root}>
      <span>Collection</span>
      <div className={styles.collection_title_wrapper}>
        <Icon name="collection" className={styles.collection_title_icon} />

        <span className={!selectedCollection ? styles.no_selection : undefined}>
          {selectedCollection?.title
            ? selectedCollection.title
            : 'No Selected Collection'}
        </span>

        {!isTreeVisible ? (
          <IconButton
            aria-label="Show collection tree"
            className={styles.edit_icon}
            onClick={() => setIsTreeVisible(true)}
            iconName="edit"
          />
        ) : (
          <IconButton
            aria-label="Clear selected collection"
            className={styles.edit_icon}
            onClick={() => handleTreeNodeClick(undefined)}
            iconName="close"
          />
        )}
      </div>
      {isTreeVisible ? (
        <SubclipCollectionTree
          selectedCollectionId={collectionId}
          onSelectionChanged={handleTreeNodeClick}
        />
      ) : null}
    </div>
  );
}
